import React, { useState, useEffect } from 'react';
import { formatRome } from '../lib/datetime';
import { api, useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Plus,
  Pin,
  MessageSquare,
  Send,
  Trash2,
  Loader2,
  AlertTriangle,
  Star,
  Clock,
  User,
  Building2
} from 'lucide-react';

const roleLabels = {
  super_admin: 'Admin',
  hotel_manager: 'Manager',
  capo_manutentore: 'Capo Man.',
  technician: 'Tecnico',
  housekeeping: 'Housekeeping',
  frontoffice: 'Front Office'
};

const priorityConfig = {
  normale: { label: 'Normale', color: 'bg-slate-100 text-slate-700', icon: null },
  importante: { label: 'Importante', color: 'bg-amber-100 text-amber-800', icon: Star },
  urgente: { label: 'Urgente', color: 'bg-red-100 text-red-800', icon: AlertTriangle }
};

const BoardPage = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [commentText, setCommentText] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [sending, setSending] = useState(null);
  const [formData, setFormData] = useState({
    title: '', content: '', hotel_id: '', priority: 'normale', pinned: false
  });

  const canPost = ['super_admin', 'hotel_manager', 'capo_manutentore', 'sede'].includes(user?.role);

  useEffect(() => {
    fetchPosts();
    fetchHotels();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data } = await api.get('/board');
      setPosts(data);
    } catch (err) {
      toast.error('Errore nel caricamento bacheca');
    } finally {
      setLoading(false);
    }
  };

  const fetchHotels = async () => {
    try {
      const { data } = await api.get('/hotels');
      setHotels(data);
    } catch (e) {
      console.log('Could not fetch hotels');
    }
  };

  const handleCreate = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Titolo e contenuto obbligatori');
      return;
    }
    try {
      setSending('create');
      await api.post('/board', {
        ...formData,
        hotel_id: formData.hotel_id || null
      });
      toast.success('Post pubblicato');
      setCreateOpen(false);
      setFormData({ title: '', content: '', hotel_id: '', priority: 'normale', pinned: false });
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella pubblicazione');
    } finally {
      setSending(null);
    }
  };

  const handleComment = async (postId) => {
    const text = commentText[postId]?.trim();
    if (!text) return;
    try {
      setSending(postId);
      await api.post(`/board/${postId}/comment`, { text });
      setCommentText(prev => ({ ...prev, [postId]: '' }));
      fetchPosts();
    } catch (err) {
      toast.error('Errore nel commento');
    } finally {
      setSending(null);
    }
  };

  const handleDelete = async (postId) => {
    try {
      await api.delete(`/board/${postId}`);
      toast.success('Post eliminato');
      fetchPosts();
    } catch (err) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleTogglePin = async (post) => {
    try {
      await api.put(`/board/${post.id}`, { pinned: !post.pinned });
      fetchPosts();
    } catch (err) {
      toast.error('Errore');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 1) return 'Adesso';
    if (diff < 60) return `${diff}m fa`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h fa`;
    return formatRome(d, { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getHotelName = (hotelId) => {
    if (!hotelId) return 'Tutti gli hotel';
    const h = hotels.find(h => h.id === hotelId);
    return h ? h.name : 'Hotel';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="board-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Bacheca</h1>
          <p className="text-slate-500 mt-1">Comunicazioni interne e avvisi per il team</p>
        </div>
        {canPost && (
          <Button onClick={() => setCreateOpen(true)} className="bg-[#1e3a5f] hover:bg-[#152c4a]" data-testid="new-post-button">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Avviso
          </Button>
        )}
      </div>

      {/* Posts */}
      {posts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg font-medium">Nessun avviso in bacheca</p>
            <p className="text-slate-400 text-sm mt-1">Pubblica il primo avviso per il team</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {posts.map(post => {
            const pCfg = priorityConfig[post.priority] || priorityConfig.normale;
            const PIcon = pCfg.icon;
            const isAuthor = post.author_id === user?.id;
            const isAdmin = ['super_admin', 'sede'].includes(user?.role);
            const commentsExpanded = expandedComments[post.id];
            const commentCount = (post.comments || []).length;

            return (
              <Card
                key={post.id}
                className={`border-slate-200 transition-all ${post.pinned ? 'border-l-4 border-l-[#d4af37] bg-amber-50/30' : ''} ${post.priority === 'urgente' ? 'border-l-4 border-l-red-500' : ''} ${post.priority === 'importante' ? 'border-l-4 border-l-amber-500' : ''}`}
                data-testid={`post-${post.id}`}
              >
                <CardContent className="p-5">
                  {/* Post header */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {post.pinned && <Pin className="w-3.5 h-3.5 text-[#d4af37] flex-shrink-0" />}
                        <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{post.title}</h3>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {post.author_name}
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{roleLabels[post.author_role] || post.author_role}</Badge>
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(post.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {getHotelName(post.hotel_id)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {pCfg.label !== 'Normale' && (
                        <Badge className={`${pCfg.color} text-xs`}>
                          {PIcon && <PIcon className="w-3 h-3 mr-1" />}
                          {pCfg.label}
                        </Badge>
                      )}
                      {(isAuthor || isAdmin) && (
                        <>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleTogglePin(post)} title={post.pinned ? 'Rimuovi pin' : 'Fissa in cima'}>
                            <Pin className={`w-3.5 h-3.5 ${post.pinned ? 'text-[#d4af37]' : 'text-slate-400'}`} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDelete(post.id)} data-testid={`delete-post-${post.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap mb-4">{post.content}</p>

                  <Separator className="mb-3" />

                  {/* Comments toggle */}
                  <button
                    className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#1e3a5f] transition-colors"
                    onClick={() => setExpandedComments(prev => ({ ...prev, [post.id]: !prev[post.id] }))}
                    data-testid={`toggle-comments-${post.id}`}
                  >
                    <MessageSquare className="w-4 h-4" />
                    {commentCount > 0 ? `${commentCount} commenti` : 'Commenta'}
                  </button>

                  {/* Comments section */}
                  {commentsExpanded && (
                    <div className="mt-3 space-y-3">
                      {(post.comments || []).map(c => (
                        <div key={c.id} className="flex gap-3 pl-2 border-l-2 border-slate-200">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-xs font-semibold text-slate-800">{c.author_name}</span>
                              <Badge variant="outline" className="text-[10px] px-1 py-0">{roleLabels[c.author_role] || c.author_role}</Badge>
                              <span className="text-[10px] text-slate-400">{formatDate(c.created_at)}</span>
                            </div>
                            <p className="text-sm text-slate-600">{c.text}</p>
                          </div>
                        </div>
                      ))}

                      {/* Add comment */}
                      <div className="flex gap-2 pt-1">
                        <Input
                          value={commentText[post.id] || ''}
                          onChange={(e) => setCommentText(prev => ({ ...prev, [post.id]: e.target.value }))}
                          placeholder="Scrivi un commento..."
                          className="flex-1 h-9 text-sm"
                          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(post.id); }}}
                          data-testid={`comment-input-${post.id}`}
                        />
                        <Button
                          size="sm"
                          className="h-9 bg-[#1e3a5f] hover:bg-[#152c4a]"
                          onClick={() => handleComment(post.id)}
                          disabled={sending === post.id || !commentText[post.id]?.trim()}
                          data-testid={`send-comment-${post.id}`}
                        >
                          {sending === post.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Post Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Avviso</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Titolo</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Oggetto dell'avviso"
                data-testid="post-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Contenuto</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Scrivi il tuo avviso..."
                rows={5}
                data-testid="post-content-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Visibilita</Label>
                <Select
                  value={formData.hotel_id || 'all'}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, hotel_id: v === 'all' ? '' : v }))}
                >
                  <SelectTrigger data-testid="post-hotel-select">
                    <SelectValue placeholder="Tutti gli hotel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli hotel</SelectItem>
                    {hotels.map(h => (
                      <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priorita</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger data-testid="post-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normale">Normale</SelectItem>
                    <SelectItem value="importante">Importante</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.pinned}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, pinned: v }))}
                  data-testid="post-pinned-switch"
                />
                <Label className="text-sm text-slate-600">Fissa in cima alla bacheca</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
              <Button
                onClick={handleCreate}
                disabled={sending === 'create'}
                className="bg-[#1e3a5f] hover:bg-[#152c4a]"
                data-testid="submit-post-button"
              >
                {sending === 'create' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Pubblica
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BoardPage;
